/**
 * AmbientLayoutWrapper — Auto-cycling renderer for ambient display.
 *
 * DRR-183 Phase 2+Polish: Cycles through configured renderers at a configurable interval.
 * CRITICAL events interrupt rotation — overlay stays until acknowledged.
 * Constellation view wired via useLiveConstellation for "By Team" rotation.
 *
 * Renderers cycle: spatial → kanban → constellation (configurable via st-0-2 Setting config).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Group,
  Text,
  Badge,
  SegmentedControl,
  Transition,
} from '@mantine/core';
import {
  IconMap,
  IconLayoutKanban,
  IconUsers,
  IconPlayerPause,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { SpatialRenderer, type SpatialCoordinate } from '../renderers/SpatialRenderer';
import { KanbanRenderer, type KanbanFieldConfig } from '../renderers/KanbanRenderer';
import {
  ConstellationView,
  type ConstellationViewNode,
  type ConstellationViewEdge,
  useLiveConstellation,
} from '@ferp/workflow-forms';
import type { PrimitiveItem } from '../../hooks/usePrimitiveData';

export type AmbientRendererType = 'spatial' | 'kanban' | 'constellation';

export interface AmbientRotationConfig {
  renderers: AmbientRendererType[];
  intervalSeconds: number;
}

const DEFAULT_ROTATION: AmbientRotationConfig = {
  renderers: ['spatial', 'kanban'],
  intervalSeconds: 30,
};

const RENDERER_LABELS: Record<AmbientRendererType, { icon: React.ReactNode; label: string }> = {
  spatial: { icon: <IconMap size={14} />, label: 'By Location' },
  kanban: { icon: <IconLayoutKanban size={14} />, label: 'By Status' },
  constellation: { icon: <IconUsers size={14} />, label: 'By Team' },
};

interface AmbientLayoutWrapperProps {
  items: PrimitiveItem[];
  rotation?: Partial<AmbientRotationConfig>;
  pauseRotation?: boolean;
  traversalLens?: 'staff' | 'resident';
  onTraversalLensChange?: (lens: 'staff' | 'resident') => void;
  coordinates?: SpatialCoordinate[];
  floorPlanUrl?: string;
  maxHeight?: string;
}

function spatialFieldAdapter(item: PrimitiveItem) {
  return {
    name: (item.name as string) ?? 'Unnamed',
    subtitle: (item.status as string) ?? undefined,
    safetyLevel: item.safetyLevel as string,
  };
}

function kanbanFieldAdapter(item: PrimitiveItem): KanbanFieldConfig {
  return {
    name: (item.name as string) ?? 'Unnamed',
    subtitle: (item.description as string) ?? undefined,
    status: item.status as string,
    safetyLevel: item.safetyLevel as string,
  };
}

export function AmbientLayoutWrapper({
  items,
  rotation: rotationOverride,
  pauseRotation = false,
  traversalLens = 'resident',
  onTraversalLensChange,
  coordinates,
  floorPlanUrl,
  maxHeight = 'calc(100vh - 120px)',
}: AmbientLayoutWrapperProps) {
  const rotation = useMemo(
    () => ({ ...DEFAULT_ROTATION, ...rotationOverride }),
    [rotationOverride],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [mounted, setMounted] = useState(true);

  const activeRenderer = rotation.renderers[activeIndex % rotation.renderers.length];

  // Constellation data — load for the first subject in the task list
  const firstSubjectId = useMemo(() => {
    for (const item of items) {
      const sid = item.subjectActorId as string | undefined;
      if (sid) return sid;
    }
    return '';
  }, [items]);

  const { constellation } = useLiveConstellation({
    centerId: firstSubjectId,
    centerName: firstSubjectId ? 'Subject' : '',
    skip: !firstSubjectId || !rotation.renderers.includes('constellation'),
  });

  // Auto-cycle through renderers
  useEffect(() => {
    if (isPaused || pauseRotation || rotation.renderers.length <= 1) return;

    const timer = setInterval(() => {
      setMounted(false);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % rotation.renderers.length);
        setMounted(true);
      }, 300);
    }, rotation.intervalSeconds * 1000);

    return () => clearInterval(timer);
  }, [isPaused, pauseRotation, rotation.renderers.length, rotation.intervalSeconds]);

  const handleManualSwitch = useCallback((renderer: string) => {
    const idx = rotation.renderers.indexOf(renderer as AmbientRendererType);
    if (idx >= 0) {
      setMounted(false);
      setTimeout(() => {
        setActiveIndex(idx);
        setMounted(true);
        setIsPaused(true);
      }, 200);
    }
  }, [rotation.renderers]);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const rendererContent = useMemo(() => {
    switch (activeRenderer) {
      case 'spatial':
        return (
          <SpatialRenderer
            items={items}
            locationField="location"
            fieldAdapter={spatialFieldAdapter}
            getId={(item) => item.id as string}
            maxHeight={maxHeight}
            coordinates={coordinates}
            floorPlanUrl={floorPlanUrl}
          />
        );
      case 'kanban':
        return (
          <KanbanRenderer
            items={items}
            columnField="status"
            fieldAdapter={kanbanFieldAdapter}
            getId={(item) => item.id as string}
            maxHeight={maxHeight}
          />
        );
      case 'constellation':
        if (!constellation) {
          return (
            <Box p="xl" ta="center">
              <IconUsers size={48} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed" size="lg" mt="md">
                No subject selected for constellation view
              </Text>
              <Text c="dimmed" size="sm">
                Constellation shows when tasks have subject actors
              </Text>
            </Box>
          );
        }
        return (
          <ConstellationView
            center={constellation.center as ConstellationViewNode}
            nodes={constellation.nodes as ConstellationViewNode[]}
            edges={constellation.edges as ConstellationViewEdge[]}
            mode="instance"
            height={parseInt(maxHeight) || 500}
          />
        );
      default:
        return null;
    }
  }, [activeRenderer, items, maxHeight, coordinates, floorPlanUrl, constellation]);

  const selectorData = rotation.renderers.map((r) => ({
    value: r,
    label: RENDERER_LABELS[r]?.label ?? r,
  }));

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Control bar */}
      <Group justify="space-between" px="md" py="xs">
        <Group gap="xs">
          <SegmentedControl
            value={activeRenderer}
            onChange={handleManualSwitch}
            data={selectorData}
            size="xs"
          />

          {rotation.renderers.length > 1 && (
            <Badge
              color={isPaused || pauseRotation ? 'yellow' : 'green'}
              variant="light"
              size="lg"
              style={{ cursor: 'pointer' }}
              onClick={togglePause}
              leftSection={
                isPaused || pauseRotation
                  ? <IconPlayerPause size={12} />
                  : <IconPlayerPlay size={12} />
              }
            >
              {isPaused ? 'Paused' : pauseRotation ? 'Alert' : `${rotation.intervalSeconds}s`}
            </Badge>
          )}
        </Group>

        {onTraversalLensChange && (
          <SegmentedControl
            value={traversalLens}
            onChange={(v) => onTraversalLensChange(v as 'staff' | 'resident')}
            data={[
              { value: 'resident', label: 'Resident View' },
              { value: 'staff', label: 'Staff View' },
            ]}
            size="xs"
          />
        )}
      </Group>

      {/* Renderer area with fade transition */}
      <Box style={{ flex: 1, overflow: 'hidden' }} px="md" pb="md">
        <Transition
          mounted={mounted}
          transition="fade"
          duration={300}
        >
          {(styles) => (
            <Box style={{ ...styles, height: '100%' }}>
              {rendererContent}
            </Box>
          )}
        </Transition>
      </Box>
    </Box>
  );
}
